import { createApp, ref, watch } from "vue";
import "./assets/app.css";
import App from "./App.vue";
import router from "./router/index";
import "@persolog/theme/fonts";

import Keycloak, { type KeycloakConfig, type KeycloakInitOptions } from "keycloak-js";

import { createI18n } from "vue-i18n";
import { sharedMessages } from "@persolog/i18n";
import { createPinia } from "pinia";

// primevue components
import PrimeVue from "primevue/config";
import Aura from "@primevue/themes/aura";
import Menubar from "primevue/menubar";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Menu from "primevue/menu";
import DataView from "primevue/dataview";
import DataTable from "primevue/datatable";
import Badge from "primevue/badge";
import OverlayBadge from "primevue/overlaybadge";
import Card from "primevue/card";
import InputChips from "primevue/inputchips";
import Chip from "primevue/chip";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import Message from "primevue/message";
import Divider from "primevue/divider";
import Panel from "primevue/panel";
import Dialog from "primevue/dialog";
import ToolTip from "primevue/tooltip";
import Skeleton from "primevue/skeleton";
import Toolbar from "primevue/toolbar";
import ToggleButton from "primevue/togglebutton";
import SelectButton from "primevue/selectbutton";
import TreeSelect from "primevue/treeselect";
import MultiSelect from "primevue/multiselect";
import InputNumber from "primevue/inputnumber";
import ProgressSpinner from "primevue/progressspinner";
import Tabs from "primevue/tabs";
import TabList from "primevue/tablist";
import Tab from "primevue/tab";
import TabPanels from "primevue/tabpanels";
import TabPanel from "primevue/tabpanel";
import Textarea from "primevue/textarea";
import Select from "primevue/select";
import Stepper from "primevue/stepper";
import StepList from "primevue/steplist";
import StepPanels from "primevue/steppanels";
import StepItem from "primevue/stepitem";
import Step from "primevue/step";
import StepPanel from "primevue/steppanel";
import Galleria from "primevue/galleria";
import Column from "primevue/column";
import ConfirmDialog from "primevue/confirmdialog";
import ConfirmPopup from "primevue/confirmpopup";
import SplitButton from "primevue/splitbutton";
import Toast from "primevue/toast";
import SpeedDial from "primevue/speeddial";
import Avatar from "primevue/avatar";
import FileUpload from "primevue/fileupload";
import ToggleSwitch from "primevue/toggleswitch";
import Timeline from "primevue/timeline";
import Accordion from "primevue/accordion";
import AccordionHeader from "primevue/accordionheader";
import AccordionContent from "primevue/accordioncontent";
import AccordionPanel from "primevue/accordionpanel";
import InputGroup from "primevue/inputgroup";
import TieredMenu from "primevue/tieredmenu";
import FloatLabel from "primevue/floatlabel";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import ScrollTop from "primevue/scrolltop";
import Fieldset from "primevue/fieldset";
import Slider from "primevue/slider";
import Carousel from "primevue/carousel";
import Image from "primevue/image";
import Inplace from "primevue/inplace";
import Popover from "primevue/popover";
import IftaLabel from "primevue/iftalabel";
import RadioButton from "primevue/radiobutton";
import DatePicker from "primevue/datepicker";
import InputMask from "primevue/inputmask";

import BadgeDirective from "primevue/badgedirective";
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";

// custom components
import MainMenu from "../src/components/common/MainMenu.vue";
import Footer from "@persolog/ui/Footer.vue";
import TitleHeadline from "../src/components/common/TitleHeadline.vue";
import { getSafeRedirect, getTrainerDetails, initializeTlds } from "./utils/GeneralUtils";
import { loadHubSpot } from "@persolog/utils";
import { usePreferencesStore } from "./stores/preferences";
import type { Language } from "./types/PersologOnlineTypes";

export const i18n = createI18n<false>({
    legacy: false,
    locale: "de",
    fallbackLocale: "en",
    globalInjection: true,
    inheritLocale: true,
    messages: sharedMessages,
});

export const trainerDetails = ref();

export const validTlds = ref(new Set<string>());
initializeTlds();

const config: KeycloakConfig = {
    url: import.meta.env.VITE_APP_KEYCLOAK_URL,
    realm: import.meta.env.VITE_APP_KEYCLOAK_REALM,
    clientId: "profile-administration-frontend",
};

const initOptions: KeycloakInitOptions = {
    onLoad: "check-sso",
    checkLoginIframe: false,
    scope: "email openid roles",
};

export const keycloak = new Keycloak(config);

keycloak
    .init(initOptions)
    .then(async (auth: boolean) => {
        console.log("Authentication status:", auth);

        setInterval(async () => {
            if (keycloak.authenticated) {
                try {
                    const refreshed = await keycloak.updateToken(30);
                    if (refreshed) {
                        console.log("Token successfully refreshed");
                    }
                } catch (err) {
                    console.warn("Token refresh failed", err);
                    const safeRedirect = getSafeRedirect(globalThis.location.href);
                    await keycloak.logout({ redirectUri: safeRedirect });
                }
            }
        }, 60_000);

        /** Verifies authentication and fetch trainer details */
        if (auth) {
            console.log("Authenticated");
            trainerDetails.value = await getTrainerDetails();
        }

        const app = createApp(App);
        const pinia = createPinia();

        const preferencesStore = usePreferencesStore(pinia);
        preferencesStore.initLanguage(i18n.global.availableLocales as Language[]);
        i18n.global.locale.value = preferencesStore.selectedLanguage;

        // Make the keycloak instance available throughout your Vue components
        app.config.globalProperties.$keycloak = keycloak;

        // Import theme system and get default preset
        const { persologDefault } = await import("./composables/useTheme");

        app.use(i18n)
            .use(pinia)
            .use(router)
            .use(PrimeVue, {
                theme: {
                    base: Aura,
                    preset: persologDefault,
                    options: {
                        prefix: "p",
                        darkModeSelector: "light",
                        // PrimeVue must sit in a layer BELOW Tailwind's utilities: unlayered CSS
                        // always beats layered CSS regardless of specificity, so with cssLayer
                        // disabled every Tailwind utility silently lost against a component style.
                        cssLayer: {
                            name: "primevue",
                            order: "theme, base, asciidoc, primevue, components, utilities",
                        },
                    },
                },
                ripple: true,
                pt: {
                    // Keep all tabs in the sequential tab order so keyboard users can reach
                    // inactive tabs with Tab key, not just arrow keys.
                    tab: {
                        root: { tabindex: "0" },
                    },
                },
            });

        // add components
        app.component("Menubar", Menubar);
        app.component("InputText", InputText);
        app.component("Button", Button);
        app.component("Menu", Menu);
        app.component("DataView", DataView);
        app.component("Card", Card);
        app.component("InputChips", InputChips);
        app.component("Checkbox", Checkbox);
        app.component("Chip", Chip);
        app.component("Badge", Badge);
        app.component("OverlayBadge", OverlayBadge);
        app.component("Tag", Tag);
        app.component("Message", Message);
        app.component("Divider", Divider);
        app.component("Panel", Panel);
        app.component("Dialog", Dialog);
        app.component("ScrollTop", ScrollTop);
        app.component("Skeleton", Skeleton);
        app.component("Toolbar", Toolbar);
        app.component("ToggleButton", ToggleButton);
        app.component("SelectButton", SelectButton);
        app.component("TreeSelect", TreeSelect);
        app.component("MultiSelect", MultiSelect);
        app.component("InputNumber", InputNumber);
        app.component("ProgressSpinner", ProgressSpinner);
        app.component("Tabs", Tabs);
        app.component("TabList", TabList);
        app.component("Tab", Tab);
        app.component("TabPanels", TabPanels);
        app.component("TabPanel", TabPanel);
        app.component("ToggleSwitch", ToggleSwitch);
        app.component("Textarea", Textarea);
        app.component("DataTable", DataTable);
        app.component("Column", Column);
        app.component("Galleria", Galleria);
        app.component("ConfirmDialog", ConfirmDialog);
        app.component("ConfirmPopup", ConfirmPopup);
        app.component("SplitButton", SplitButton);
        app.component("Toast", Toast);
        app.component("SpeedDial", SpeedDial);
        app.component("Avatar", Avatar);
        app.component("FileUpload", FileUpload);
        app.component("Timeline", Timeline);
        app.component("Accordion", Accordion);
        app.component("AccordionHeader", AccordionHeader);
        app.component("AccordionContent", AccordionContent);
        app.component("AccordionPanel", AccordionPanel);
        app.component("InputGroup", InputGroup);
        app.component("TieredMenu", TieredMenu);
        app.component("FloatLabel", FloatLabel);
        app.component("IconField", IconField);
        app.component("InputIcon", InputIcon);
        app.component("ScrollTop", ScrollTop);
        app.component("Fieldset", Fieldset);
        app.component("Select", Select);
        app.component("Slider", Slider);
        app.component("Stepper", Stepper);
        app.component("StepList", StepList);
        app.component("StepPanels", StepPanels);
        app.component("StepItem", StepItem);
        app.component("Step", Step);
        app.component("StepPanel", StepPanel);
        app.component("Carousel", Carousel);
        app.component("Image", Image);
        app.component("Inplace", Inplace);
        app.component("Popover", Popover);
        app.component("IftaLabel", IftaLabel);
        app.component("RadioButton", RadioButton);
        app.component("DatePicker", DatePicker);
        app.component("InputMask", InputMask);

        app.component("MainMenu", MainMenu);
        app.component("Footer", Footer);
        app.component("TitleHeadline", TitleHeadline);

        app.directive("badge", BadgeDirective);
        app.directive("tooltip", ToolTip);

        app.use(ConfirmationService);
        app.use(ToastService);

        app.mount("#app");

        // HubSpot tracking + Customer Agent chat (RDT-307). Only stages with a portal id in their
        // .env.<mode> load it; localhost, ci and dev stay free of third-party scripts.
        loadHubSpot(import.meta.env.VITE_APP_HUBSPOT_PORTAL_ID);

        // Initialize theme system after app is mounted
        const { useTheme: initThemeSystem } = await import("./composables/useTheme");
        const { setTheme } = initThemeSystem();

        preferencesStore.initTheme();
        setTheme(preferencesStore.selectedTheme);

        const updateLangAttributes = (locale: string) => {
            document.documentElement.lang = locale;
            let meta = document.querySelector('meta[name="Content-Language"]');
            if (meta) {
                meta.setAttribute("content", locale);
                return;
            }

            meta = document.createElement("meta");
            meta.setAttribute("name", "Content-Language");
            document.head.appendChild(meta);
            meta.setAttribute("content", locale);
        };

        updateLangAttributes(i18n.global.locale.value);

        watch(i18n.global.locale, (locale) => {
            updateLangAttributes(locale);
        });
    })
    .catch((error) => {
        if (error.error === "login_required") {
            localStorage.removeItem("newAccount");
            location.reload();
            return;
        }
        console.error("Authenticated Failed");
        console.error(error);
    });
